import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus,
  FiX,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiImage,
  FiBook,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiSearch,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  adminCourseAPI,
  OnlineCourse,
  OnlineCourseType,
  CourseEnrollment,
  getOnlineCourseImageURL,
} from '../services/api';
import './BookingManagement.css';
import './Dashboard.css';

interface CoursesManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const COURSE_TABS: { key: OnlineCourseType; label: string }[] = [
  { key: 'private', label: 'Private' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'vip_feedback', label: 'VIP Feedback' },
];

const CoursesManagement: React.FC<CoursesManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [courseTypeTab, setCourseTypeTab] = useState<OnlineCourseType>('private');
  const [mainTab, setMainTab] = useState<'courses' | 'enrollments'>('courses');
  const [courses, setCourses] = useState<OnlineCourse[]>([]);
  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [uploadingCourse, setUploadingCourse] = useState(false);
  const [savingEnrollment, setSavingEnrollment] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState<number | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<CourseEnrollment | null>(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [enrollmentCourseTypeFilter, setEnrollmentCourseTypeFilter] = useState<string>('all');

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    sort_order: 0,
    is_active: true,
    image_file: null as File | null,
    course_type: 'private' as OnlineCourseType,
  });

  const fetchCourses = useCallback(async () => {
    try {
      setLoadingCourses(true);
      const data = await adminCourseAPI.listCourses(courseTypeTab);
      setCourses(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCourses(false);
    }
  }, [courseTypeTab]);

  const fetchEnrollments = useCallback(async () => {
    try {
      setLoadingEnrollments(true);
      const filters: { status?: string; course_type?: OnlineCourseType } = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (enrollmentCourseTypeFilter !== 'all') {
        filters.course_type = enrollmentCourseTypeFilter as OnlineCourseType;
      }
      const data = await adminCourseAPI.listEnrollments(filters);
      setEnrollments(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingEnrollments(false);
    }
  }, [statusFilter, enrollmentCourseTypeFilter]);

  useEffect(() => {
    if (mainTab === 'courses') void fetchCourses();
  }, [mainTab, fetchCourses]);

  useEffect(() => {
    if (mainTab === 'enrollments') void fetchEnrollments();
  }, [mainTab, fetchEnrollments]);

  useEffect(() => {
    const loadPending = async () => {
      try {
        const p = await adminCourseAPI.listEnrollments({ status: 'pending' });
        setPendingCount(p.length);
      } catch {
        setPendingCount(0);
      }
    };
    loadPending();
  }, [enrollments.length]);

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      sort_order: 0,
      is_active: true,
      image_file: null,
      course_type: courseTypeTab,
    });
    setEditingCourseId(null);
  };

  const handleSaveCourse = async () => {
    if (!courseForm.title.trim()) {
      alert('Title is required');
      return;
    }
    try {
      setUploadingCourse(true);
      const formData = new FormData();
      formData.append('title', courseForm.title.trim());
      formData.append('description', courseForm.description);
      formData.append(
        'course_type',
        editingCourseId ? courseForm.course_type : courseTypeTab
      );
      formData.append('sort_order', String(Number(courseForm.sort_order) || 0));
      formData.append('is_active', courseForm.is_active ? 'true' : 'false');
      if (courseForm.image_file) {
        formData.append('image_file', courseForm.image_file);
      }
      if (editingCourseId) {
        await adminCourseAPI.updateCourse(editingCourseId, formData);
      } else {
        await adminCourseAPI.createCourse(formData);
      }
      resetCourseForm();
      setShowCourseModal(false);
      fetchCourses();
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to save course');
    } finally {
      setUploadingCourse(false);
    }
  };

  const handleEditCourse = (c: OnlineCourse) => {
    setCourseForm({
      title: c.title,
      description: c.description || '',
      sort_order: c.sort_order ?? 0,
      is_active: c.is_active,
      image_file: null,
      course_type: c.course_type,
    });
    setEditingCourseId(c.id);
    setShowCourseModal(true);
  };

  const handleDeleteCourse = async (id: number) => {
    if (!window.confirm('Delete this course? Enrollments history may become inconsistent.')) return;
    try {
      await adminCourseAPI.deleteCourse(id);
      fetchCourses();
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to delete');
    }
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollment) return;
    try {
      setSavingEnrollment(true);
      await adminCourseAPI.updateEnrollment(selectedEnrollment.id, {
        status: enrollmentStatus,
        admin_notes: adminNotes,
      });
      setShowEnrollmentModal(false);
      setSelectedEnrollment(null);
      fetchEnrollments();
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to update');
    } finally {
      setSavingEnrollment(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { color: string; icon: typeof FiAlertCircle; text: string }> = {
      pending: { color: '#ffc107', icon: FiAlertCircle, text: 'Pending' },
      approved: { color: '#28a745', icon: FiCheckCircle, text: 'Approved' },
      rejected: { color: '#dc3545', icon: FiXCircle, text: 'Rejected' },
      cancelled: { color: '#6c757d', icon: FiXCircle, text: 'Cancelled' },
    };
    const cfg = map[status] || map.pending;
    const Icon = cfg.icon;
    return (
      <span className="status-badge" style={{ color: cfg.color, borderColor: cfg.color }}>
        {React.createElement(Icon as React.ComponentType<{ size?: number }>, { size: 14 })}
        <span>{cfg.text}</span>
      </span>
    );
  };

  const typeLabel = (t: OnlineCourseType) =>
    COURSE_TABS.find((x) => x.key === t)?.label || t;

  const filteredEnrollments = useMemo(() => {
    let rows = [...enrollments];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.user_email.toLowerCase().includes(q) ||
          (r.user_name && r.user_name.toLowerCase().includes(q)) ||
          r.course_title.toLowerCase().includes(q) ||
          r.contact_address.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [enrollments, searchQuery]);

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userEmail}
          organization="NAATI"
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="booking-management">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Courses Management</h1>
              <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 8 }}>
                Manage online courses (Private, Workshop, VIP Feedback) and enrollment requests.
              </p>
            </motion.div>

            <div className="tabs-container" style={{ flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className={`tab-button ${mainTab === 'courses' ? 'active' : ''}`}
                onClick={() => setMainTab('courses')}
              >
                Courses
              </button>
              <button
                type="button"
                className={`tab-button ${mainTab === 'enrollments' ? 'active' : ''}`}
                onClick={() => setMainTab('enrollments')}
              >
                Enrollments ({pendingCount} pending)
              </button>
            </div>

            {mainTab === 'courses' && (
              <>
                <div className="tabs-container" style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
                  {COURSE_TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`tab-button ${courseTypeTab === t.key ? 'active' : ''}`}
                      onClick={() => setCourseTypeTab(t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="tab-content">
                  <div className="dashboard-welcome">
                    <div>
                      <h2>{typeLabel(courseTypeTab)} courses</h2>
                      <p>Create and edit courses shown to users under Online Courses.</p>
                    </div>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        resetCourseForm();
                        setCourseForm((f) => ({ ...f, course_type: courseTypeTab }));
                        setShowCourseModal(true);
                      }}
                    >
                      {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <span>Add course</span>
                    </button>
                  </div>

                  {loadingCourses ? (
                    <div className="loading-container">
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                        className: 'spinner',
                        size: 32,
                      })}
                      <p>Loading courses...</p>
                    </div>
                  ) : courses.length === 0 ? (
                    <div className="empty-state">
                      {React.createElement(FiBook as React.ComponentType<{ size?: number }>, { size: 48 })}
                      <p>No courses in this category yet.</p>
                    </div>
                  ) : (
                    <div className="services-grid">
                      {courses.map((c) => (
                        <motion.div
                          key={c.id}
                          className="service-card"
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <div className="service-image">
                            {c.image_garage_key ? (
                              <img src={getOnlineCourseImageURL(c.id)} alt={c.title} />
                            ) : (
                              <div className="service-image-placeholder">
                                {React.createElement(FiImage as React.ComponentType<{ size?: number }>, { size: 48 })}
                              </div>
                            )}
                            {!c.is_active && <div className="service-inactive-badge">Inactive</div>}
                          </div>
                          <div className="service-info">
                            <h3>{c.title}</h3>
                            <p className="service-description">{c.description || '—'}</p>
                            <p style={{ fontSize: 12, color: '#888' }}>Order: {c.sort_order}</p>
                            <div className="service-actions">
                              <button type="button" className="btn-icon" onClick={() => handleEditCourse(c)} title="Edit">
                                {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                              </button>
                              <button
                                type="button"
                                className="btn-icon danger"
                                onClick={() => handleDeleteCourse(c.id)}
                                title="Delete"
                              >
                                {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {mainTab === 'enrollments' && (
              <div className="tab-content">
                <div className="dashboard-welcome">
                  <div>
                    <h2>Enrollment requests</h2>
                    <p>Approve or reject applications. User email comes from their account.</p>
                  </div>
                </div>
                <div className="requests-table-filters">
                  <div className="filter-group">
                    <div className="search-input-wrapper">
                      <span className="search-icon">
                        {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                      </span>
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search user, course, address..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="filter-group">
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All statuses</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <select
                      className="filter-select"
                      value={enrollmentCourseTypeFilter}
                      onChange={(e) => setEnrollmentCourseTypeFilter(e.target.value)}
                    >
                      <option value="all">All course types</option>
                      <option value="private">Private</option>
                      <option value="workshop">Workshop</option>
                      <option value="vip_feedback">VIP Feedback</option>
                    </select>
                  </div>
                </div>

                {loadingEnrollments ? (
                  <div className="loading-container">
                    {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                      className: 'spinner',
                      size: 32,
                    })}
                    <p>Loading enrollments...</p>
                  </div>
                ) : filteredEnrollments.length === 0 ? (
                  <div className="empty-state">
                    {React.createElement(FiBook as React.ComponentType<{ size?: number }>, { size: 48 })}
                    <p>No enrollments match filters.</p>
                  </div>
                ) : (
                  <div className="requests-table-container">
                    <table className="requests-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Course</th>
                          <th>Type</th>
                          <th>Message</th>
                          <th>Phone</th>
                          <th>Address</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEnrollments.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <div className="user-info">
                                <span className="user-email">{r.user_email}</span>
                                {r.user_name && r.user_name !== r.user_email && (
                                  <span className="user-name">{r.user_name}</span>
                                )}
                              </div>
                            </td>
                            <td>{r.course_title}</td>
                            <td>{typeLabel(r.course_type)}</td>
                            <td className="message-cell">{r.applicant_message || '—'}</td>
                            <td>{r.contact_phone || '—'}</td>
                            <td className="message-cell">{r.contact_address || '—'}</td>
                            <td>{getStatusBadge(r.status)}</td>
                            <td>{formatDate(r.created_at)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-icon"
                                title="Review"
                                onClick={() => {
                                  setSelectedEnrollment(r);
                                  setEnrollmentStatus(r.status);
                                  setAdminNotes(r.admin_notes || '');
                                  setShowEnrollmentModal(true);
                                }}
                              >
                                {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showCourseModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !uploadingCourse && (setShowCourseModal(false), resetCourseForm())}
          >
            <motion.div
              className="modal-content booking-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingCourseId ? 'Edit course' : 'Add course'}</h2>
                <button
                  type="button"
                  className="modal-close"
                  disabled={uploadingCourse}
                  onClick={() => !uploadingCourse && (setShowCourseModal(false), resetCourseForm())}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>
              <div className="modal-body">
                {editingCourseId && (
                  <div className="form-group">
                    <label>Course type</label>
                    <select
                      value={courseForm.course_type}
                      onChange={(e) =>
                        setCourseForm({ ...courseForm, course_type: e.target.value as OnlineCourseType })
                      }
                      disabled={uploadingCourse}
                    >
                      <option value="private">Private</option>
                      <option value="workshop">Workshop</option>
                      <option value="vip_feedback">VIP Feedback</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    disabled={uploadingCourse}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    rows={4}
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    disabled={uploadingCourse}
                  />
                </div>
                <div className="form-group">
                  <label>Sort order</label>
                  <input
                    type="number"
                    min={0}
                    value={courseForm.sort_order}
                    onChange={(e) =>
                      setCourseForm({ ...courseForm, sort_order: parseInt(e.target.value, 10) || 0 })
                    }
                    disabled={uploadingCourse}
                  />
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingCourse}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        if (!f.type.startsWith('image/')) {
                          alert('Please select an image');
                          return;
                        }
                        if (f.size > 10 * 1024 * 1024) {
                          alert('Max 10MB');
                          return;
                        }
                        setCourseForm({ ...courseForm, image_file: f });
                      }
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={courseForm.is_active}
                      onChange={(e) => setCourseForm({ ...courseForm, is_active: e.target.checked })}
                      disabled={uploadingCourse}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={uploadingCourse}
                  onClick={() => {
                    setShowCourseModal(false);
                    resetCourseForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!courseForm.title.trim() || uploadingCourse}
                  onClick={handleSaveCourse}
                >
                  {uploadingCourse ? 'Saving...' : editingCourseId ? 'Update' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEnrollmentModal && selectedEnrollment && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !savingEnrollment && setShowEnrollmentModal(false)}
          >
            <motion.div
              className="modal-content booking-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Review enrollment</h2>
                <button type="button" className="modal-close" onClick={() => setShowEnrollmentModal(false)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>
              <div className="modal-body">
                <div className="request-details">
                  <div className="detail-row">
                    <span className="detail-label">User</span>
                    <span className="detail-value">{selectedEnrollment.user_email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Course</span>
                    <span className="detail-value">{selectedEnrollment.course_title}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type</span>
                    <span className="detail-value">{typeLabel(selectedEnrollment.course_type)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Message</span>
                    <span className="detail-value">{selectedEnrollment.applicant_message || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone</span>
                    <span className="detail-value">{selectedEnrollment.contact_phone || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Address / contact</span>
                    <span className="detail-value">{selectedEnrollment.contact_address || '—'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={enrollmentStatus}
                    onChange={(e) => setEnrollmentStatus(e.target.value)}
                    disabled={savingEnrollment}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Admin notes</label>
                  <textarea
                    rows={4}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    disabled={savingEnrollment}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={savingEnrollment}
                  onClick={() => setShowEnrollmentModal(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn-primary" disabled={savingEnrollment} onClick={handleUpdateEnrollment}>
                  {savingEnrollment ? 'Saving...' : 'Save decision'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CoursesManagement;
